import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LogOut } from "lucide-react";
import { vaultApi, vaultToken, timeAgo } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BonusCard } from "@/components/BonusCard";

const FILTERS = [["all", "All"], ["free_sc", "Free SC"], ["no_deposit", "No Deposit"], ["deposit", "Deposit Match"], ["daily_login", "Daily Login"]];

export default function Vault() {
  const [bonuses, setBonuses] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("recent");
  const navigate = useNavigate();

  useEffect(() => {
    if (!vaultToken()) return navigate("/");
    vaultApi().get("/bonuses").then((r) => setBonuses(r.data)).catch(() => {
      localStorage.removeItem("vault_token");
      navigate("/");
    });
  }, [navigate]);

  const list = useMemo(() => {
    const out = bonuses.filter((b) => (type === "all" || b.bonus_type === type) && `${b.casino} ${b.title}`.toLowerCase().includes(q.toLowerCase()));
    return sort === "value" ? [...out].sort((a, b) => b.est_value - a.est_value) : out;
  }, [bonuses, q, type, sort]);

  const signOut = () => { localStorage.removeItem("vault_token"); navigate("/"); };

  return (
    <div className="relative">
      <Header count={bonuses.length} />
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-400/90">Your vault</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-2" data-testid="vault-title">All live offers</h1>
            {bonuses[0] && <p className="text-sm text-slate-400 mt-3 font-mono" data-testid="vault-last-updated">Last update {timeAgo(bonuses[0].updated_at)}</p>}
          </div>
          <button onClick={signOut} data-testid="vault-signout-button" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><LogOut size={16} />Sign out</button>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center sticky top-16 z-20 py-4 bg-[#07090E]/90 backdrop-blur-xl">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search casinos or offers" data-testid="bonus-search-input" className="w-full h-12 rounded-full bg-[#151B28] border border-white/10 pl-11 pr-4 text-sm outline-none focus:border-emerald-500/50" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setType(k)} data-testid={`bonus-filter-${k.replace("_", "-")}`} className={`whitespace-nowrap rounded-full px-4 h-10 text-sm font-semibold border transition-colors ${type === k ? "bg-emerald-500 text-[#07090E] border-emerald-500" : "border-white/10 text-slate-300 hover:border-emerald-500/40"}`}>{l}</button>
            ))}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="bonus-sort-select" className="h-10 rounded-full bg-[#151B28] border border-white/10 px-4 text-sm">
            <option value="recent">Recently updated</option>
            <option value="value">Highest value</option>
          </select>
        </div>

        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="vault-bonus-grid">
          {list.map((b, i) => <BonusCard key={b.id} bonus={b} testid={`vault-bonus-card-${i}`} />)}
        </div>
        {!list.length && <p className="text-slate-500 mt-10" data-testid="vault-empty">No offers match your filters.</p>}
      </main>
      <Footer />
    </div>
  );
}
